import { LOCALE_NAMES } from "@repo/shared";
import type { FinancialSituationModel, LocaleCode } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  rowControl,
  Table,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api/_client";
import * as fsmApi from "@/lib/api/financial-situation-models";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import { useDeleteFinancialSituationModel } from "@/lib/hooks/use-financial-situation-models";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./models-table.i18n";

type ModelsTableProps = {
  models: FinancialSituationModel[];
  onEdit: (model: FinancialSituationModel) => void;
};

export function ModelsTable({ models, onEdit }: ModelsTableProps) {
  const t = useT(T);
  const deleteMutation = useDeleteFinancialSituationModel();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingModel, setDeletingModel] = useState<FinancialSituationModel | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  // QL-168 — a model still used by flows shows why straight away, before the confirmation.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: fsmApi.checkDelete,
    onAllowed: (modelId) => {
      const model = models.find((m) => m.id === modelId);
      if (!model) return;
      setDeletingModel(model);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) => showAlert(t.error, reason || t.failedToDeleteModel, "destructive"),
  });

  const handleDeleteConfirm = async () => {
    if (!deletingModel) return;

    try {
      await deleteMutation.mutateAsync(deletingModel.id);
    } catch (err) {
      showAlert(
        t.error,
        err instanceof ApiError ? err.message : t.failedToDeleteModel,
        "destructive",
      );
    }
    setDeletingModel(null);
  };

  if (models.length === 0) {
    return (
      <div className="text-center py-12 border rounded-lg">
        <p className="text-muted-foreground">{t.noModelsYet}</p>
      </div>
    );
  }

  return (
    <>
      <Table variant="navigation">
        <TableHeader>
          <TableRow>
            <TableHead>{t.name}</TableHead>
            <TableHead className="w-28">{t.language}</TableHead>
            <TableHead className="text-right">{t.actions}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {models.map((model: FinancialSituationModel) => (
            <TableRow key={model.id}>
              <TableCellLink
                className="font-medium"
                to={`/settings/financial-situation-models/${model.id}`}
              >
                {model.name}
              </TableCellLink>
              <TableCell className="text-sm text-muted-foreground">
                {model.language
                  ? LOCALE_NAMES[model.language as LocaleCode] || model.language
                  : "-"}
              </TableCell>
              <TableCell className={cn("text-right", rowControl)}>
                <div className="flex items-center justify-end gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => onEdit(model)}
                    tooltip={t.edit}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    loading={checkingId === model.id}
                    onClick={() => requestDelete(model.id)}
                    tooltip={t.delete}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteModel}
        description={t.deleteModelConfirmation(deletingModel?.name ?? "")}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDeleteConfirm}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
